// TelcoTrack — one meaning, one glyph.
// Paths are existing Heroicons outline strokes (viewBox 0 0 24 24, stroke currentColor)
// already used by the sidebar (`nav-icons`), row actions (`action-icon`), or filter triggers.
// Sidebar tabs, filter icons, and icon-only toolbars read this map. Do not paste the same
// meaning's path into a template.
//
// | meaning | icon | where |
// | stavba / objekt | objects | sidebar Objekty, filtr Typ |
// | polygon | polygon | sidebar Polygony, filtr Polygon |
// | rozvaděč | rozvadec | záložky Rozvaděče (uzly) |
// | budova | budova | záložky Budova (stejná budova jako Inženýring — office building) |
// | pobočka | home | filtry Zodpovědná pobočka (dům) |
// | fáze | status | filtr Fáze (check v kruhu) |
// | plán polygonu | flag | app-filter-polygon-plan-status |
// | milníky | milestones | filtry Milníky (clipboard-check, stejný tah jako Příprava) |
// | uživatel | users | sidebar Uživatelé, filtry Uživatel |
// | firma | companies | sidebar Firmy, filtr Firma |
// | datum | calendar | filtr data, záložky Kalendář |
// | hledání | search | lupa (akce search) |
// | mapa | maps | sidebar Mapa, řádka „Na mapě“ |
// | fotka | photo | fotoaparát |
// | export | export | akce export |
// | číslo (č.p.) | hash | mřížka # |
// | adresa / ulice | pin | špendlík |
// | kontakt | kontakty | sidebar Kontakty (karta) |
// | materiál / sklad | materials | krychle Sklady |
// | zařízení | equipment | sidebar Zařízení |
// | poznámka | note | dokument deníku (ne tužka) |
// | HP | hp | text „HP“ jako u filtru atributů |
// | parcela | parcela | kosočtverec majetku (icon.service PROPERTY) |
// | akvizice | tag | štítek; záložka Akvizice a filtr Stav ACQ |
// | záfuk | zafuk | mřížka délky (ne mapa, ne šipky převodu) |
// | svar / provaření | splice | dva spojené uzly (i spojkoviště) |
//
// Left sharing a stroke on purpose: budova and Inženýring are the same office building;
// milníky reuse the Příprava clipboard; akvizice reuses the Ceny price-tag (no second label glyph).

import { ACTION_ICON_PATHS } from '../components/ui/icons/action-icon';
import { NAV_ICON_PATHS, type NavIconId } from './nav-icons';

export const ICON_MEANING = {
  objekt: 'objects',
  polygon: 'polygon',
  rozvadec: 'rozvadec',
  budova: 'budova',
  pobocka: 'home',
  faze: 'status',
  plan: 'flag',
  milniky: 'milestones',
  uzivatel: 'users',
  firma: 'companies',
  datum: 'calendar',
  hledani: 'search',
  mapa: 'maps',
  fotka: 'photo',
  export: 'export',
  cislo: 'hash',
  adresa: 'pin',
  kontakt: 'kontakty',
  material: 'materials',
  sklad: 'materials',
  zarizeni: 'equipment',
  poznamka: 'note',
  hp: 'hp',
  parcela: 'parcela',
  akvizice: 'tag',
  zafuk: 'zafuk',
  svar: 'splice',
} as const;

export type IconMeaning = keyof typeof ICON_MEANING;

/** Glyph id stored in templates and in `ICON_MEANING`. */
export type IconName =
  | (typeof ICON_MEANING)[IconMeaning]
  | 'roles'
  | 'warehouse-from'
  | 'warehouse-to'
  | 'construction'
  | 'drawing'
  | 'regions'
  | 'cross'
  | 'spider';

type PathGlyph = { readonly kind: 'paths'; readonly paths: readonly string[] };
type MarkGlyph = { readonly kind: 'mark'; readonly text: string };
type SpiderGlyph = { readonly kind: 'spider' };
export type IconGlyph = PathGlyph | MarkGlyph | SpiderGlyph;

const HOUSE =
  'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6';
const CHECK = 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z';
const CALENDAR =
  'M8 7V3m8 4V3m-9 8h10m-11 9h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v11a2 2 0 002 2z';
const FLAG =
  'M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9';
const HASH = 'M7 20l4-16m2 16l4-16M6 9h14M4 15h14';
const PIN = [
  'M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z',
  'M15 11a3 3 0 11-6 0 3 3 0 016 0z',
] as const;
const PHOTO = [
  'M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z',
  'M15 13a3 3 0 11-6 0 3 3 0 016 0z',
] as const;
/** Price tag — shell „Ceny“ and Stav ACQ (one label glyph in the set). */
const TAG = [
  'M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z',
  'M6 6h.008v.008H6V6Z',
] as const;
/** Cable grid used for délka záfuku — not the folded map and not převod arrows. */
const ZAFUK = 'M4 8h16M4 16h16M8 4v16M16 4v16';
/** Property diamond from map icon.service (PROPERTY). */
const PARCELA = 'M12 6l6 6-6 6-6-6z';
const SPLICE = [
  'M8 12 m-3.5 0 a3.5 3.5 0 1 0 7 0 a3.5 3.5 0 1 0 -7 0',
  'M16 12 m-3.5 0 a3.5 3.5 0 1 0 7 0 a3.5 3.5 0 1 0 -7 0',
  'M11.5 12H12.5',
] as const;
const ROZVADEC =
  'M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z';
const ROLES =
  'M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2';
const CONSTRUCTION =
  'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-4.95 2.05M14.5 14.5l5 5m0-5l-5 5M9.5 20H4a1 1 0 01-1-1v-.5A6.5 6.5 0 019.5 12';
const DRAWING = 'M12 3L2 8l10 5 10-5-10-5zM2 14l10 5 10-5M2 19l10 5 10-5';
const REGIONS =
  'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9';
const WAREHOUSE_FROM =
  'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1';
const WAREHOUSE_TO =
  'M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1';

function nav(id: NavIconId): PathGlyph {
  return { kind: 'paths', paths: NAV_ICON_PATHS[id] };
}

function one(d: string): PathGlyph {
  return { kind: 'paths', paths: [d] };
}

export const ICON_GLYPHS: Record<IconName, IconGlyph> = {
  objects: nav('objects'),
  polygon: nav('polygons'),
  rozvadec: one(ROZVADEC),
  budova: nav('inzenyring'),
  home: one(HOUSE),
  status: one(CHECK),
  flag: one(FLAG),
  milestones: nav('priprava'),
  users: nav('users'),
  companies: nav('companies'),
  calendar: one(CALENDAR),
  search: one(ACTION_ICON_PATHS.search),
  maps: nav('maps'),
  photo: { kind: 'paths', paths: PHOTO },
  export: one(ACTION_ICON_PATHS.export),
  hash: one(HASH),
  pin: { kind: 'paths', paths: PIN },
  kontakty: nav('kontakty'),
  materials: nav('materials'),
  equipment: nav('equipment'),
  note: nav('diary'),
  hp: { kind: 'mark', text: 'HP' },
  parcela: one(PARCELA),
  tag: { kind: 'paths', paths: TAG },
  zafuk: one(ZAFUK),
  splice: { kind: 'paths', paths: SPLICE },
  roles: one(ROLES),
  'warehouse-from': one(WAREHOUSE_FROM),
  'warehouse-to': one(WAREHOUSE_TO),
  construction: one(CONSTRUCTION),
  drawing: one(DRAWING),
  regions: one(REGIONS),
  cross: one(ACTION_ICON_PATHS.close),
  spider: { kind: 'spider' },
};

export const ICON_LABELS: Record<IconName, string> = {
  objects: 'Objekt',
  polygon: 'Polygon',
  rozvadec: 'Rozvaděč',
  budova: 'Budova',
  home: 'Pobočka',
  status: 'Fáze',
  flag: 'Plán',
  milestones: 'Milníky',
  users: 'Uživatel',
  companies: 'Firma',
  calendar: 'Datum',
  search: 'Hledat',
  maps: 'Mapa',
  photo: 'Fotka',
  export: 'Export',
  hash: 'Číslo',
  pin: 'Adresa',
  kontakty: 'Kontakt',
  materials: 'Materiál',
  equipment: 'Zařízení',
  note: 'Poznámka',
  hp: 'HP',
  parcela: 'Parcela',
  tag: 'Akvizice',
  zafuk: 'Záfuk',
  splice: 'Svar',
  roles: 'Role',
  'warehouse-from': 'Odkud',
  'warehouse-to': 'Kam',
  construction: 'Stavba',
  drawing: 'Výkres',
  regions: 'Území',
  cross: 'Křížení',
  spider: 'Návaznosti',
};

export function iconGlyph(name: IconName | '' | null | undefined): IconGlyph | null {
  if (!name) return null;
  return ICON_GLYPHS[name] ?? null;
}

/** Stroke paths for a glyph. Empty for the HP text mark and the návaznosti spider. */
export function iconPaths(name: IconName | '' | null | undefined): readonly string[] {
  const glyph = iconGlyph(name);
  if (!glyph || glyph.kind !== 'paths') return [];
  return glyph.paths;
}

export function iconLabel(name: IconName | '' | null | undefined): string {
  if (!name) return '';
  return ICON_LABELS[name] ?? '';
}
